import React from "react";
import {Badge, Empty, Tabs} from "antd";
import "./TaskStatus.scss";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import {connect} from "react-redux";
import {
    AutoSizer,
    CellMeasurer,
    CellMeasurerCache,
    List,
    ListRowProps,
    ScrollParams,
    Size,
} from "react-virtualized";
import {Nullable} from "@data-force/app";
import classNames from "classnames";
import StoreState from "../../../interfaces/store/storeState";
import SearchField from "../../common/SearchField/SearchField";
import {GetTaskListData, Task, TaskPhase} from "../../../interfaces/store/actions/payload/task";
import {JobEntity, PhaseConfigNormalized} from "../../../interfaces/store/actions/payload/job";
import Spinner from "../../common/Spinner/Spinner";
import {getOpeningUrl} from "../../../utils/common";
import {getTaskList} from "../../../store/actions/task";
import {Cursor} from "../../../interfaces/store/cursor";
import {GetTaskListAction} from "../../../interfaces/store/actions/task";
import {Editors, TaskPhaseStatusColors} from "../../../interfaces/common";
import {TaskStatusMap} from "../../../utils/constants";

const { TabPane } = Tabs;

interface State {
    searchValue: string
}

interface StateProps {
    translate: TranslateFunction
    tasks: Task[]
    current: JobEntity
    isLoading: boolean
    editors: Editors
    phaseConfigList: PhaseConfigNormalized
    cursor: Nullable<Cursor>
}

interface OwnProps {
    tab: string
}

interface DispatchProps {
    getTaskList(payload?: GetTaskListData): GetTaskListAction
}

type Props = OwnProps & StateProps & DispatchProps;

class TaskStatus extends React.Component<Props, State> {
    private static readonly translationPath = "components.JobDrawer.TaskStatus.TaskStatus";

    private static readonly cache = new CellMeasurerCache({
        defaultHeight: 54,
        minHeight: 54,
        fixedHeight: true,
        minWidth: 1000,
        keyMapper: () => 1,
    });

    state: State = {
        searchValue: "",
    };

    constructor(props: Props) {
        super(props);

        this.noContentRenderer = this.noContentRenderer.bind(this);

        this.onScrollHandler = this.onScrollHandler.bind(this);

        this.searchTaskHandler = this.searchTaskHandler.bind(this);
    }

    componentDidMount() {
        this.props.getTaskList();
    }

    /**
     * Scroll handler will load more projects to list
     * @param {number} scrollHeight
     * @param {number} clientHeight
     * @param {number} scrollTop
     */
    onScrollHandler({scrollHeight, clientHeight, scrollTop}: ScrollParams) {
        if (scrollHeight !== 0
            && ((Math.floor(scrollTop) === (scrollHeight - clientHeight)) || (Math.ceil(scrollTop) === (scrollHeight - clientHeight)))) {
            const {cursor}: Props = this.props;
            this.props.getTaskList({
                cursor: cursor!,
                name: this.state.searchValue,
            });
        }
    }

    /**
     * Get row of titles for all task list columns
     * @return React.ReactNode
     */
    getTitleRow(): React.ReactNode {
        const {translate}: Props = this.props;
        return (
            <div className="df-task-list-header">
                <div className="df-task-list-status">
                    {translate(`${TaskStatus.translationPath}.statusHeader.text`)}
                </div>
                <div className="df-task-list-name">
                    {translate(`${TaskStatus.translationPath}.taskNameHeader.text`)}
                </div>
            </div>
        );
    }

    /**
     * Based on task stats, we find matching phase status and use it for badge info
     * @param {Task} task
     * @return {React.ReactNode}
     */
    getTaskItemStatus(task: Task): React.ReactNode {
        const {current: {name}, translate}: Props = this.props;
        const phaseStats: Nullable<TaskPhase> = task.phases.find(
            (phase: TaskPhase): boolean => phase.name === name,
        );
        let taskStatus = "ready";
        let taskStatusCmp: React.ReactNode = <></>;
        if (phaseStats) {
            const {status}: TaskPhase = phaseStats;
            taskStatus = status.toLowerCase();

            taskStatusCmp = (
                <Badge
                    color={TaskStatusMap[status] as keyof TaskPhaseStatusColors}
                    text={translate(`enums.TaskStatus.${taskStatus}.text`)}
                />
            );
        }

        return taskStatusCmp;
    }

    /**
     * Show this when there is no data for the list
     * @return React.ReactNode
     */
    noContentRenderer(): React.ReactNode {
        return this.props.isLoading
            ? <div/>
            : <Empty description={<Translate id={`${TaskStatus.translationPath}.noTaskData.text`}/>}/>;
    }

    /**
     * Search task from task list
     */
    searchTaskHandler(name: string): void {
        this.props.getTaskList({
            name,
        });
    }

    /**
     * Open appropriate editor for task currently clicked
     * @param {string} id
     * @param {string} payload
     * @param {string} editorUrl
     */
    openEditor({id, payload}: Task, editorUrl: string): void {
        if (editorUrl) {
            window.open(`${editorUrl}/${payload}/${id}`);
        }
    }

    /**
     * Contributor list cell renderer
     * @param {string} key
     * @param {React.CSSProperties} style
     * @param {React.Component} parent
     * @param {number} index
     */
    renderRow({
        key, style, parent, index,
    }: ListRowProps): React.ReactNode {
        const taskData: Task = this.props.tasks[index];
        const {current, editors, phaseConfigList} : Props = this.props;
        const editorUrl: string = getOpeningUrl(editors, phaseConfigList, current);
        const taskClasses = classNames({
            "df-task-list-row": true,
            "df-task-list-row-disabled": !editorUrl,
        });

        return (
            <CellMeasurer
                cache={TaskStatus.cache}
                key={key}
                parent={parent}
            >
                <div style={{...style}} key={key} className="df-task-list">
                    <div
                        className={taskClasses}
                        onDoubleClick={this.openEditor.bind(this, taskData, editorUrl)}
                    >
                        <div className="df-task-list-status">
                            {this.getTaskItemStatus(taskData)}
                        </div>
                        <div className="df-task-list-name">
                            {taskData.name}
                        </div>
                    </div>
                </div>
            </CellMeasurer>
        );
    }

    render() {
        const {translate, tasks = [], isLoading}: Props = this.props;
        const {translationPath}: typeof TaskStatus = TaskStatus;
        return (
            <TabPane {...this.props}>
                <SearchField
                    onSearch={this.searchTaskHandler}
                    inputProps={{
                        placeholder: translate(`${translationPath}.searchTasks.text`) as string,
                        "aria-label": translate(`${translationPath}.searchTasks.text`) as string,
                    }}
                />
                {this.getTitleRow()}
                <div className="df-task-list-container">
                    {isLoading && (<Spinner size="large"/>)}
                    <AutoSizer>
                        {({ width, height }: Size): React.ReactNode => (
                            <List
                                height={height}
                                width={width}
                                rowRenderer={this.renderRow.bind(this)}
                                noContentRenderer={this.noContentRenderer}
                                deferredMeasurementCache={TaskStatus.cache}
                                rowCount={tasks.length}
                                overscanRowCount={10}
                                rowHeight={54}
                                onScroll={this.onScrollHandler}
                                aria-label={translate(`${translationPath}.listOfTasks.text`) as string}
                            />
                        )}
                    </AutoSizer>
                </div>
            </TabPane>
        );
    }
}

function mapStateToProps(state: StoreState): StateProps {
    return {
        translate: getTranslate(state.localize),
        isLoading: state.mask.isLoading,
        current: state.job.selectedJob || {} as JobEntity,
        tasks: state.task.list as Task[],
        editors: state.serviceProperties.editors,
        phaseConfigList: state.job.phaseConfigList,
        cursor: state.task.cursor,
    };
}

const mapDispatchToProps: DispatchProps = {
    getTaskList,
};

export default connect(mapStateToProps, mapDispatchToProps)(TaskStatus);
